import {
  Button,
  CreateButton,
  Dropdown,
  Icons,
  List,
  Menu,
  notification,
  Popconfirm,
  ShowButton,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from '@pankod/refine-antd';
import { IResourceComponentsProps, useCan } from '@pankod/refine-core';
import { SearchWithDateRange } from 'components/search/SearchWithDateRange';
import CustomTableWithPagination from 'components/table/custom-table';
import { EStatus } from 'enums';
import { IPurchaseStockCorrection, IQueryOptionals, PaginationHandler } from 'interfaces';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SearchIndex } from 'services/meilisearch';
import {
  deletePurchaseStockCorrection,
  getPurchaseStockCorrections,
  updatePurchaseStockCorrectionStatus,
} from 'services/purchase';
import MetaData from 'utilities/metadata';
import { formatCurrency, getDateRangeOfLastXMonths } from 'utilities/shared';
import styles from './style.module.css';

const { Text } = Typography;

export const PurchaseStockCorrectionsList: React.FC<IResourceComponentsProps> = () => {
  const [purchaseStockCorrections, setPurchaseStockCorrections] = useState<
    IPurchaseStockCorrection[]
  >([]);
  const [totalPurchaseStockCorrections, setTotalPurchaseStockCorrections] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [dateRange, setDateRange] = useState<{ startDate?: string; endDate?: string }>(() => {
    const [startDate, endDate] = getDateRangeOfLastXMonths(3);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  });
  const [actionsDisable, setActionsDisable] = useState<Record<string, boolean>>({});
  const { data: canAccessDelete } = useCan({
    resource: 'purchases',
    action: 'delete',
    params: { field: 'id' },
  });
  const query = useMemo<IQueryOptionals>(() => {
    return {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      searchString: searchText,
      start: page,
      limit,
    };
  }, [dateRange.startDate, dateRange.endDate, searchText, page, limit]);

  const fetchPurchaseStockCorrections = useCallback(async (query: IQueryOptionals) => {
    setIsLoading(true);
    try {
      const response = await getPurchaseStockCorrections(query);
      setPurchaseStockCorrections(response.data);
      setTotalPurchaseStockCorrections(response.total);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchPurchaseStockCorrections(query);
  }, [fetchPurchaseStockCorrections, query]);

  const deleteConfirmHandler = async (id: string) => {
    try {
      await deletePurchaseStockCorrection(id);
      await fetchPurchaseStockCorrections({
        startDate: query.startDate,
        endDate: query.endDate,
      });
      notification.success({
        message: 'Success',
        description: 'Successfully deleted a purchase stock correction',
      });
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
  };

  const onSearch = async (values: {
    startDate?: string;
    endDate?: string;
    searchString: string;
  }) => {
    setDateRange({ startDate: values.startDate, endDate: values.endDate });
    setSearchText(values.searchString);
    setPage(1);
  };

  const pageHeaderButtons = (
    <>
      <CreateButton type="primary" />
    </>
  );

  const onPageChange: PaginationHandler = async (page: number, pageSize: number) => {
    setPage(page);
    setLimit(pageSize);
  };

  const statusStyles: Record<EStatus | string, { textColor: string; backgroundColor: string }> = {
    [EStatus.CONFIRMED]: {
      textColor: '#1E88E5', // Blue for text
      backgroundColor: '#1E88E50D', // Light blue for background
    },
    [EStatus.IN_PROGRESS]: {
      textColor: '#FFA500', // Orange for text
      backgroundColor: '#FFA5001A', // Light orange for background
    },
    [EStatus.COMPLETED]: {
      textColor: '#43A047', // Green for text
      backgroundColor: '#43A0470D', // Light green for background
    },
    [EStatus.CANCELLED]: {
      textColor: '#E53935', // Strong red for text
      backgroundColor: '#E539350D', // Light red for background
    },
  };

  const getStatusStyles = (status: string) => {
    const normalizedStatus = status.trim().toLowerCase();
    const matchedStatus = Object.entries(EStatus).find(
      ([, value]) => value.toLowerCase() === normalizedStatus
    )
      ? statusStyles[status as EStatus]
      : statusStyles[EStatus.CANCELLED]; // Default to cancel if not matched.

    return matchedStatus || { textColor: '#000000', backgroundColor: '#FFFFFF' }; // Fallback to default
  };

  return (
    <List headerProps={{ extra: pageHeaderButtons }}>
      <MetaData />
      <SearchWithDateRange
        placeholder="Search by Supplier, ID, Tags, Product, Warehouse"
        size="large"
        className={styles['filter-section__search']}
        index={SearchIndex.Purchase}
        groups={['supplier', 'id', 'product', 'tag', 'warehouse']}
        onSearch={onSearch}
      />

      <CustomTableWithPagination
        total={totalPurchaseStockCorrections}
        page={page}
        limit={limit}
        onPageChange={onPageChange}
      >
        <Table
          className={styles['custom-table']}
          dataSource={purchaseStockCorrections}
          loading={isLoading}
          rowKey="id"
          scroll={{ x: true }}
          // onChange={onPageChange}
          pagination={false}
          style={{
            boxShadow: '0px 1px 0px 0px #EDF3FF inset',
            fontFamily: 'Nunito Sans',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '22px',
          }}
        >
          <Table.Column<IPurchaseStockCorrection> dataIndex="id" title="Order ID" />
          <Table.Column<IPurchaseStockCorrection>
            dataIndex="purchaseDate"
            title="Date"
            render={(_, record): React.ReactNode => {
              const date = new Date(record.purchaseDate).toLocaleDateString('en-GB');
              return <Text>{date}</Text>;
            }}
          />
          <Table.Column
            dataIndex="purchaseStatus"
            title="Status"
            render={(status: string) => {
              const { textColor, backgroundColor } = getStatusStyles(status);

              return (
                <Tag
                  style={{
                    color: textColor,
                    backgroundColor: backgroundColor,
                    height: '24px',
                    padding: '0px 8px',
                    gap: '4px',
                    borderRadius: '8px',
                    opacity: 1,
                    border: 'none',
                    fontFamily: 'Nunito Sans',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '22px',
                  }}
                >
                  {status}
                </Tag>
              );
            }}
          />

          <Table.Column<IPurchaseStockCorrection>
            dataIndex={['supplier', 'name']}
            title="Supplier Name"
          />
          <Table.Column<IPurchaseStockCorrection>
            dataIndex={['warehouse', 'name']}
            title="Warehouse"
            render={(value, _record) => {
              return <Text>{value}</Text>;
            }}
          />
          <Table.Column<IPurchaseStockCorrection>
            dataIndex="totalAmount"
            title="Amount"
            render={(_, record) => formatCurrency(record.totalAmount)}
            sorter={(a, b) => a.totalAmount - b.totalAmount}
          />
          <Table.Column<IPurchaseStockCorrection>
            title="Actions"
            dataIndex="actions"
            key="actions"
            width={120}
            render={(_, record) => {
              const text = <span>User: {record?.createdBy?.name}</span>;
              return (
                <Space>
                  <ShowButton size="small" recordItemId={record.id} hideText />
                  <Popconfirm
                    title="Are you sure?"
                    okText="Delete"
                    onConfirm={() => deleteConfirmHandler(record.id)}
                    disabled={!canAccessDelete?.can}
                  >
                    <Button
                      size="small"
                      danger
                      icon={<Icons.DeleteOutlined />}
                      disabled={!canAccessDelete?.can}
                    />
                  </Popconfirm>
                  <Tooltip title={text}>
                    <Button size="small" icon={<Icons.InfoOutlined />} />
                  </Tooltip>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <StatusUpdateAction
                      record={record}
                      onStatusUpdate={() => fetchPurchaseStockCorrections(query)}
                      disabled={actionsDisable[record.id]}
                      setActionsDisable={setActionsDisable}
                    />
                  </div>
                </Space>
              );
            }}
          />
        </Table>
      </CustomTableWithPagination>
    </List>
  );
};

type StatusUpdateActionProps = {
  record: IPurchaseStockCorrection;
  onStatusUpdate: () => Promise<void>;
  disabled: boolean;
  setActionsDisable: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
};

const StatusUpdateAction: React.FC<StatusUpdateActionProps> = ({
  record,
  onStatusUpdate,
  disabled,
  setActionsDisable,
}) => {
  const updateStatus = async (id: string, status: EStatus) => {
    setActionsDisable((prev) => ({ ...prev, [id]: true }));
    try {
      await updatePurchaseStockCorrectionStatus(id, status);
      onStatusUpdate();
      notification.success({
        message: 'Success',
        description: 'Successfully changed status',
      });
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: err instanceof Error ? err.message : null,
      });
    } finally {
      setActionsDisable(({ [id]: _, ...rest }) => rest);
    }
  };

  const getDisabledState = (status: EStatus) => {
    switch (status) {
      case EStatus.CONFIRMED:
        return {
          confirmedDisabled: true,
          inProgressDisabled: false,
          completedDisabled: false,
          cancelledDisabled: false,
        };
      case EStatus.IN_PROGRESS:
        return {
          confirmedDisabled: true,
          inProgressDisabled: true,
          completedDisabled: false,
          cancelledDisabled: false,
        };
      case EStatus.COMPLETED:
        return {
          confirmedDisabled: true,
          inProgressDisabled: true,
          completedDisabled: true,
          cancelledDisabled: false,
        };
      case EStatus.CANCELLED:
        return {
          confirmedDisabled: true,
          inProgressDisabled: true,
          completedDisabled: true,
          cancelledDisabled: true,
        };
      default:
        return {
          confirmedDisabled: false,
          inProgressDisabled: false,
          completedDisabled: false,
          cancelledDisabled: false,
        };
    }
  };

  const moreMenu = (record: IPurchaseStockCorrection) => {
    const { confirmedDisabled, inProgressDisabled, completedDisabled, cancelledDisabled } =
      getDisabledState(record.purchaseStatus as EStatus);
    return (
      <Menu mode="vertical" onClick={({ domEvent }) => domEvent.stopPropagation()}>
        <Menu.Item
          key="confirmed"
          disabled={confirmedDisabled || disabled}
          onClick={() => updateStatus(record.id, EStatus.CONFIRMED)}
        >
          Order Confirmed
        </Menu.Item>
        <Menu.Item
          key="progress"
          disabled={inProgressDisabled || disabled}
          onClick={() => updateStatus(record.id, EStatus.IN_PROGRESS)}
        >
          Delivery In Progress
        </Menu.Item>
        <Menu.Item
          key="completed"
          disabled={completedDisabled || disabled}
          onClick={() => updateStatus(record.id, EStatus.COMPLETED)}
        >
          Completed
        </Menu.Item>
        <Popconfirm
          title="Are you sure?"
          okText="Yes"
          onConfirm={() => updateStatus(record.id, EStatus.CANCELLED)}
          disabled={cancelledDisabled || disabled}
          cancelText="No"
        >
          <Menu.Item key="cancelled" disabled={cancelledDisabled || disabled}>
            Cancelled
          </Menu.Item>
        </Popconfirm>
      </Menu>
    );
  };
  return (
    <Dropdown overlay={moreMenu(record)} trigger={['click']}>
      <Icons.MoreOutlined
        onClick={(e) => e.stopPropagation()}
        style={{
          fontSize: 24,
        }}
      />
    </Dropdown>
  );
};
